import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FormikHelpers } from 'formik';
import { FC, useCallback, useEffect, useState } from 'react';

import { changePasswordAdminSchema, changePasswordSchema } from 'src/schemas/auth';
import {
  useUpdatePasswordMutation,
  useUpdateUserPasswordMutation,
} from 'src/services/auth';
import { theme } from '../../services/theme';
import { Dialog } from '../Dialog';
import { Form } from '../forms/Form';
import { FormPasswordField } from '../forms/PasswordField';
import { FormSubmitButton } from '../forms/SubmitButton';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  adminReset: boolean;
  updatedUserId?: number;
}

interface FormValues {
  currentPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

const initialValues: FormValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirm: '',
};


export const ChangePasswordModal: FC<Props> = ({
  isOpen,
  onClose,
  adminReset,
  updatedUserId,
}) => {
  const [error, setError] = useState<string>();
  const [updatePassword] = useUpdatePasswordMutation();
  const [updateUserPassword] = useUpdateUserPasswordMutation();
  const onSubmit = useCallback(
    async (
      values: FormValues,
      { setSubmitting }: FormikHelpers<FormValues>,
    ) => {
      setSubmitting(true);
      try {
        if (adminReset) {
          await updateUserPassword({
            id: updatedUserId as number,
            confirm: values.newPasswordConfirm,
            password: values.newPassword,
          }).unwrap();
        } else {
          await updatePassword({
            confirm: values.newPasswordConfirm,
            current: values.currentPassword,
            password: values.newPassword,
          }).unwrap();
        }

        onClose();
      } catch (err: any) {
        switch (err?.data?.message) {
          case 'SAME_PASSWORD_AS_OLD':
            setError('Your new password cannot be the same as your current password.');
            break;
          case 'INVALID_CURRENT_PASSWORD':
            setError('Your current password is incorrect.');
            break;
          default:
            setError('Failed to set new password.');
        }
        setSubmitting(false);
      }
    },
    [adminReset, updatedUserId],
  );

  useEffect(() => {
    if (!isOpen) {
      setError(undefined);
    }
  }, [isOpen]);

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        sx={{
          height: '500px',
          minHeight: '460px',
          padding: '30px 120px',
          width: '640px',
        }}
      >
        <Typography
          fontWeight="regular"
          sx={{
            color: '#260F26',
            fontSize: '24px',
            lineHeight: '34px',
            marginBottom: '12px',
            textAlign: 'center',
            [theme.breakpoints.down('sm')]: {
              fontSize: '18px',
              lineHeight: '20px',
            },
            [theme.breakpoints.down('mobile')]: {
              fontSize: '16px',
              lineHeight: '18px',
            },
          }}
        >
          Change password
        </Typography>
        <Form<FormValues>
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={
            adminReset ? changePasswordAdminSchema : changePasswordSchema
          }
        >
          {!adminReset && (
            <FormPasswordField
              isTooltipVisible={false}
              label="Current password"
              name="currentPassword"
              placeholder="Enter your current password"
            />
          )}
          <FormPasswordField
            label="New password"
            name="newPassword"
            placeholder="Enter your new password"
          />
          <FormPasswordField
            isTooltipVisible={false}
            label="Confirm password"
            name="newPasswordConfirm"
            sx={{ marginBottom: '12px' }}
            placeholder="Confirm your new password"
          />
          {error && (
            <Box sx={{ marginBottom: '12px' }}>
              <Typography sx={{ color: '#D7335C', fontSize: '14px', textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          )}
          <FormSubmitButton fullWidth title="Update password" />
        </Form>
      </Dialog>
    </>
  );
};
